import React from 'react';
import { Star, CheckCircle, Shield, BadgeCheck, Clock, Calendar } from 'lucide-react';

const BADGE_CONFIG = {
  verified: { icon: CheckCircle, label: 'Documents verified', color: 'text-green-600 bg-green-50' },
  insured: { icon: Shield, label: 'Insurance on file', color: 'text-blue-600 bg-blue-50' },
  dbs_checked: { icon: BadgeCheck, label: 'DBS on file', color: 'text-purple-600 bg-purple-50' },
  right_to_work: { icon: BadgeCheck, label: 'Right to work', color: 'text-teal-600 bg-teal-50' },
};

const QuoteComparisonPanel = ({ quotes = [], requestStatus, onAccept }) => {
  const canAccept = ['open', 'quoted'].includes(requestStatus);

  if (quotes.length === 0) {
    return (
      <div className="text-center py-8 text-gray-400">
        <p>No quotes received yet</p>
      </div>
    );
  }

  return (
    <div className="space-y-3">
      {quotes.map(quote => {
        const isAccepted = quote.status === 'accepted';
        const isDeclined = quote.status === 'declined';
        const rating = quote.providerRating || 0;

        return (
          <div
            key={quote._id}
            className={`rounded-xl border p-4 transition-all ${
              isAccepted ? 'bg-green-50 border-green-200' :
              isDeclined ? 'opacity-50 bg-gray-50' :
              'bg-white hover:shadow-sm'
            }`}
          >
            <div className="flex items-start justify-between gap-3">
              {/* Provider Info */}
              <div className="flex items-center gap-3 min-w-0">
                {quote.providerImage ? (
                  <img src={quote.providerImage} alt="" className="w-10 h-10 rounded-full object-cover" />
                ) : (
                  <div className="w-10 h-10 rounded-full bg-[#1F3A68] text-white flex items-center justify-center font-bold text-sm">
                    {(quote.providerName || 'P')[0]}
                  </div>
                )}
                <div className="min-w-0">
                  <h4 className="font-semibold text-gray-900 truncate">{quote.providerName}</h4>
                  <div className="flex items-center gap-1">
                    {[1, 2, 3, 4, 5].map(s => (
                      <Star key={s} size={10} className={s <= Math.round(rating) ? 'text-amber-400 fill-amber-400' : 'text-gray-300'} />
                    ))}
                    <span className="text-xs text-gray-400 ml-1">
                      {rating > 0 ? rating.toFixed(1) : '—'} ({quote.providerReviews || 0})
                    </span>
                  </div>
                </div>
              </div>

              {/* Price */}
              <div className="text-right flex-shrink-0">
                <div className="text-xl font-bold text-[#1F3A68]">£{quote.amount?.toFixed(2)}</div>
                {isAccepted && (
                  <span className="text-xs text-green-600 font-medium flex items-center gap-1 justify-end">
                    <CheckCircle size={12} /> Accepted
                  </span>
                )}
                {isDeclined && (
                  <span className="text-xs text-gray-400">Declined</span>
                )}
              </div>
            </div>

            {/* Badges */}
            {quote.providerBadges?.length > 0 && (
              <div className="flex flex-wrap gap-1 mt-2">
                {quote.providerBadges.map(badge => {
                  const config = BADGE_CONFIG[badge];
                  if (!config) return null;
                  const Icon = config.icon;
                  return (
                    <span key={badge} className={`inline-flex items-center gap-0.5 px-1.5 py-0.5 rounded-full text-[10px] font-medium ${config.color}`}>
                      <Icon size={10} /> {config.label}
                    </span>
                  );
                })}
              </div>
            )}

            {/* Quote Details */}
            {quote.description && (
              <p className="text-sm text-gray-600 mt-2">{quote.description}</p>
            )}

            <div className="flex flex-wrap gap-3 mt-2 text-xs text-gray-400">
              {quote.estimatedDuration && (
                <span className="flex items-center gap-1"><Clock size={12} /> {quote.estimatedDuration}</span>
              )}
              {quote.availableDate && (
                <span className="flex items-center gap-1"><Calendar size={12} /> Available {new Date(quote.availableDate).toLocaleDateString('en-GB')}</span>
              )}
            </div>

            {/* Accept Button */}
            {canAccept && quote.status === 'pending' && (
              <button
                onClick={() => onAccept?.(quote._id)}
                className="mt-3 w-full py-2 bg-[#1F3A68] text-white rounded-lg hover:bg-[#162d52] text-sm font-medium transition-colors"
              >
                Accept This Quote
              </button>
            )}
          </div>
        );
      })}
    </div>
  );
};

export default QuoteComparisonPanel;
